import { useState } from "react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import {
  useCreateAccountLink,
  type AccountSearchResult,
  type CreateLinkPayload,
} from "../../api";

import { AccountSearch } from "./AccountSearch";

/**
 * Inline form for creating a new AccountLink. Three relationships exposed
 * here; `subcustomer_of` is intentionally managed from the Partners feature
 * instead (matches the old admin app's separation of concerns).
 */
const REL_OPTIONS: { value: CreateLinkPayload["relationship"]; label: string }[] = [
  { value: "merged_into", label: "Merged into (this account was absorbed)" },
  { value: "split_from", label: "Split from (this account split off)" },
  { value: "continuation_of", label: "Continuation of (same business, new QB ID)" },
];

export function AccountLinkForm({
  fromQbId,
  onSuccess,
  onCancel,
}: {
  fromQbId: number;
  onSuccess: () => void;
  onCancel: () => void;
}) {
  const [relationship, setRelationship] =
    useState<CreateLinkPayload["relationship"]>("merged_into");
  const [target, setTarget] = useState<AccountSearchResult | null>(null);
  const [effective, setEffective] = useState("");
  const [note, setNote] = useState("");
  const [error, setError] = useState<string | null>(null);

  const create = useCreateAccountLink();

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!target) {
      setError("Pick a target account.");
      return;
    }
    try {
      await create.mutateAsync({
        source_qb_id: fromQbId,
        target_qb_id: target.qb_id,
        relationship,
        effective_period: effective || null,
        note: note || null,
      });
      onSuccess();
    } catch (e2) {
      setError(e2 instanceof Error ? e2.message : "Create failed");
    }
  }

  return (
    <form
      onSubmit={submit}
      className="grid grid-cols-1 gap-3 rounded-md border border-border bg-muted/30 p-4 sm:grid-cols-2"
    >
      <div className="flex flex-col gap-1">
        <label
          htmlFor="link-relationship"
          className="text-xs uppercase tracking-wider text-muted-foreground"
        >
          Relationship
        </label>
        <Select
          value={relationship}
          onValueChange={(v) => setRelationship(v as CreateLinkPayload["relationship"])}
        >
          <SelectTrigger id="link-relationship" className="text-sm">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {REL_OPTIONS.map((o) => (
              <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="flex flex-col gap-1">
        <label
          htmlFor="link-target"
          className="text-xs uppercase tracking-wider text-muted-foreground"
        >
          Target account
        </label>
        <AccountSearch id="link-target" value={target} onChange={setTarget} excludeQb={fromQbId} />
      </div>
      <div className="flex flex-col gap-1">
        <label
          htmlFor="link-effective"
          className="text-xs uppercase tracking-wider text-muted-foreground"
        >
          Effective month (optional)
        </label>
        <Input
          id="link-effective"
          type="date"
          value={effective}
          onChange={(e) => setEffective(e.target.value)}
        />
      </div>
      <div className="flex flex-col gap-1">
        <label
          htmlFor="link-note"
          className="text-xs uppercase tracking-wider text-muted-foreground"
        >
          Note (optional)
        </label>
        <Input
          id="link-note"
          type="text"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="why this link exists"
        />
      </div>
      <div className="flex items-center gap-2 sm:col-span-2">
        <Button type="submit" disabled={!target || create.isPending}>
          {create.isPending ? "Saving…" : "Add link"}
        </Button>
        <Button type="button" variant="outline" onClick={onCancel} disabled={create.isPending}>
          Cancel
        </Button>
        {error && <span className="text-xs text-destructive">{error}</span>}
      </div>
    </form>
  );
}
